import config from 'config';
import { getAuth } from 'firebase/auth';

const apiBase = config.apiUrl;

export async function startAgentStream({ content, messages = [], threadId = null, clarifications = null }) {
  // Return a config object instead of URL since we need POST
  return { content, messages, threadId, clarifications };
}

export async function openAgentStream(config, { onOpen, onToken, onComplete, onError } = {}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = `${apiBase}/agentic/agent/orchestrate/stream`;

  let lastTextFragment = '';
  let finalPayload = null;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({
        content: config.content,
        messages: config.messages,
        thread_id: config.threadId || undefined,
        clarifications: config.clarifications || undefined
      })
    });

    onOpen && onOpen();

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    let currentEventType = null;

    const processText = (text) => {
      buffer += text;
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEventType = line.substring(6).trim();
        } else if (line.startsWith('data:')) {
          const data = line.substring(5).trim();
          try {
            const parsed = JSON.parse(data);
            if (currentEventType === 'open') {
              // Expecting { ok: true, thread_id }
              try {
                onOpen && onOpen(parsed);
              } catch (e) {
                // ignore
              }
            } else if (currentEventType === 'tool_started') {
              // { tool_name, args, metadata: { tool_call_id, timestamp } }
              try {
                const payload = {
                  type: 'tool_started',
                  tool_name: parsed?.tool_name || parsed?.metadata?.tool_name,
                  args: parsed?.args,
                  metadata: parsed?.metadata || {}
                };
                onToken && onToken(payload);
              } catch (e) {
                // ignore
              }
            } else if (currentEventType === 'tool_output') {
              // { tool_name, result, metadata }
              try {
                const payload = {
                  type: 'tool_output',
                  tool_name: parsed?.tool_name || parsed?.metadata?.tool_name,
                  result: parsed?.result,
                  metadata: parsed?.metadata || {}
                };
                onToken && onToken(payload);
              } catch (e) {
                // ignore
              }
            } else if (currentEventType === 'tool_completed') {
              try {
                const payload = {
                  type: 'tool_completed',
                  tool_name: parsed?.tool_name || parsed?.metadata?.tool_name,
                  metadata: parsed?.metadata || {}
                };
                onToken && onToken(payload);
              } catch (e) {
                // ignore
              }
            } else if (currentEventType === 'final') {
              const finalText = parsed?.message || parsed?.text || parsed?.content || '';
              if (finalText && finalText.length > lastTextFragment.length) lastTextFragment = finalText;
              finalPayload = parsed;
              // Do not call onComplete here; wait until stream end to avoid duplicate completion
            } else if (currentEventType === 'reasoning') {
              const text = parsed?.text || parsed?.delta || parsed?.content || '';
              if (text) onToken && onToken({ text, type: 'reasoning' });
            } else if (currentEventType === 'error') {
              // Surface structured errors to the UI so chat can render a friendly card
              try {
                onToken && onToken({ type: 'error', ...parsed });
              } catch {
                // ignore
              }
            } else if (currentEventType === 'token' || !currentEventType) {
              const norm = normalizeToken(parsed);
              if (norm?.text) {
                lastTextFragment += norm.text;
                onToken && onToken(norm);
              }
            } // ignore other event types for now
          } catch (e) {
            // Ignore parse errors
          }
          currentEventType = null; // Reset after processing data
        }
      }
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        onComplete && onComplete({ lastText: lastTextFragment, final: finalPayload });
        break;
      }
      const text = decoder.decode(value, { stream: true });
      processText(text);
    }

    return { close: () => reader.cancel() };
  } catch (err) {
    console.error('[agentStream] Error:', err);
    onError && onError(err);
    return { close: () => {} };
  }
}

export async function stopAgentStream(threadId) {
  if (!threadId) return;
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return;
  try {
    const token = await user.getIdToken();
    const url = `${apiBase}/agentic/agent/orchestrate/stop/${encodeURIComponent(threadId)}`;
    await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      credentials: 'include'
    });
  } catch (_) {
    // ignore network errors on stop
  }
}

export async function rateChatMessage(messageId, isHelpful, note = '') {
  if (!messageId) throw new Error('messageId required');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const url = `${apiBase}/agentic/chat/messages/${encodeURIComponent(messageId)}/feedback`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ is_helpful: !!isHelpful, note: note || '' })
  });
  if (!res.ok) {
    let err = {};
    try {
      err = await res.json();
    } catch (_) {
      // ignore
    }
    const msg = err?.error || `Failed (${res.status})`;
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

// Fetch paginated list of current user's threads
export async function fetchChatThreads({ page = 1, page_size = 8 } = {}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = new URL(`${apiBase}/agentic/chat/threads`);
  url.searchParams.set('page', String(page));
  url.searchParams.set('page_size', String(page_size));

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    },
    credentials: 'include'
  });
  if (!res.ok) throw new Error(`Failed to fetch threads (${res.status})`);
  const data = await res.json();

  // Normalize response fields
  const items = data.items || data.threads || data.results || [];
  const normalized = {
    page: Number(data.page || data.current || data.page_number || page || 1),
    page_size: Number(data.page_size || data.pageSize || page_size || (Array.isArray(items) ? items.length : 0) || 10),
    num_pages: Number(
      data.num_pages ||
        data.total_pages ||
        (data.total || data.count
          ? Math.max(1, Math.ceil((data.total || data.count) / (data.page_size || data.pageSize || page_size || 10)))
          : 1)
    ),
    total: Number(data.total || data.count || (Array.isArray(items) ? items.length : 0))
  };

  return { items, ...normalized, raw: data };
}

// Fetch a full thread including ordered messages and artifacts
export async function fetchChatThread(threadId) {
  if (!threadId) throw new Error('threadId required');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = `${apiBase}/agentic/chat/threads/${encodeURIComponent(threadId)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    },
    credentials: 'include'
  });
  if (!res.ok) throw new Error(`Failed to fetch thread (${res.status})`);
  const data = await res.json();
  return data;
}

// Search current user's chat threads by title or last message preview
export async function searchChatThreads({ q, page = 1, page_size = 8 } = {}) {
  const term = (q || '').trim();
  if (!term) throw new Error('q is required');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  // Agentic namespace (authoritative)
  const baseUrl = `${apiBase}/agentic/chat/threads/search`;
  const url = new URL(baseUrl);
  url.searchParams.set('q', term);
  url.searchParams.set('page', String(page));
  url.searchParams.set('page_size', String(page_size));

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    },
    credentials: 'include'
  });

  if (!res.ok) throw new Error(`Failed to search threads (${res.status})`);
  const data = await res.json();
  const items = data.items || data.threads || data.results || [];
  const normalized = {
    page: Number(data.page || page || 1),
    page_size: Number(data.page_size || page_size || (Array.isArray(items) ? items.length : 0) || 10),
    num_pages: Number(
      data.num_pages ||
        data.total_pages ||
        (data.total || data.count
          ? Math.max(1, Math.ceil((data.total || data.count) / (data.page_size || data.pageSize || page_size || 10)))
          : 1)
    ),
    total: Number(data.total || data.count || (Array.isArray(items) ? items.length : 0))
  };
  return { items, ...normalized, raw: data };
}

// Share or unshare a chat thread
export async function shareChatThread(threadId, isPublic) {
  if (!threadId) throw new Error('threadId required');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = `${apiBase}/agentic/chat/threads/${encodeURIComponent(threadId)}/share`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    credentials: 'include',
    body: JSON.stringify({ is_public: !!isPublic })
  });

  if (!res.ok) {
    let err = {};
    try {
      err = await res.json();
    } catch (_) {
      // ignore
    }
    const msg = err?.error || `Failed to ${isPublic ? 'share' : 'unshare'} thread (${res.status})`;
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }

  return res.json();
}

// Fetch a public chat thread (no authentication required)
export async function fetchPublicChatThread(threadId, { limit = 100, offset = 0 } = {}) {
  if (!threadId) throw new Error('threadId required');

  const url = new URL(`${apiBase}/agentic/shared-reports/${encodeURIComponent(threadId)}`);
  url.searchParams.set('limit', String(limit));
  url.searchParams.set('offset', String(offset));

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  });

  if (!res.ok) {
    let err = {};
    try {
      err = await res.json();
    } catch (_) {
      // ignore
    }
    const msg = err?.error || `Failed to fetch public thread (${res.status})`;
    const e = new Error(msg);
    e.status = res.status;
    throw e;
  }

  return res.json();
}

// Keep old EventSource-based handler for backward compatibility
export function openAgentStreamOld(streamUrl, { onOpen, onToken, onComplete, onError } = {}) {
  console.log('[agentStream] STARTING with streamUrl:', streamUrl);
  const absoluteUrl = streamUrl.startsWith('http') ? streamUrl : `${apiBase}${streamUrl.startsWith('/') ? '' : '/'}${streamUrl}`;
  console.log('[agentStream] absoluteUrl:', absoluteUrl);

  const es = new EventSource(absoluteUrl, { withCredentials: true });
  console.log('[agentStream] EventSource created:', es);
  let lastTextFragment = '';

  es.addEventListener('open', (e) => {
    console.log('[agentStream] open', e);
    onOpen && onOpen(e);
  });

  // Some backends emit incremental tokens as 'token'
  es.addEventListener('token', (e) => {
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] token', data);
      const norm = normalizeToken(data);
      if (norm?.text) lastTextFragment += norm.text;
      onToken && onToken(norm);
    } catch (err) {
      // ignore malformed tokens
    }
  });

  // Handle agent-specific events: plan, planner_meta, final
  es.addEventListener('plan', (e) => {
    // Optional: surface planning as a thought token if desired
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] plan', data);
      const text = typeof data === 'string' ? data : JSON.stringify(data);
      onToken && onToken({ text, type: 'thought' });
    } catch (err) {
      // ignore
    }
  });

  es.addEventListener('planner_meta', () => {
    console.log('[agentStream] planner_meta');
    // Ignore metadata for now
  });

  es.addEventListener('final', (e) => {
    // Emit the final message as a token, then complete
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] final', data);
      const text = typeof data === 'string' ? data : data?.message || data?.text || data?.content || '';
      if (text) {
        lastTextFragment += text;
        onToken && onToken({ text });
      }
    } catch (err) {
      // ignore
    }
    onComplete && onComplete({ lastText: lastTextFragment });
    es.close();
  });

  // Generic message fallback
  es.addEventListener('message', (e) => {
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] message', data);
      const norm = normalizeToken(data);
      if (norm?.text) lastTextFragment += norm.text;
      if (norm?.text) onToken && onToken(norm);
    } catch (err) {
      // ignore
    }
  });

  // Tool events: show as thought or ignore
  es.addEventListener('tool', (e) => {
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] tool', data);
      const summary = typeof data === 'string' ? data : data?.output?.data?.summary || JSON.stringify(data);
      if (summary) onToken && onToken({ text: summary, type: 'thought' });
    } catch (err) {
      // ignore
    }
  });

  es.addEventListener('complete', () => {
    console.log('[agentStream] complete', { lastText: lastTextFragment });
    onComplete && onComplete({ lastText: lastTextFragment });
    es.close();
  });

  es.addEventListener('error', (e) => {
    console.error('[agentStream] error', e);
    onError && onError(e);
    es.close();
  });

  return es;
}

// Lightweight status check to see if orchestrator is still running for a thread
export async function fetchOrchestrateStatus(threadId) {
  if (!threadId) throw new Error('threadId required');
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = `${apiBase}/agentic/agent/orchestrate/status/${encodeURIComponent(threadId)}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`
    },
    credentials: 'include'
  });
  if (!res.ok) throw new Error(`Failed to fetch status (${res.status})`);
  // Expect { running: true|false }
  return res.json();
}

// Subscribe to future-only SSE events for a running orchestrator
export function subscribeAgentStream(threadId, { onOpen, onToken, onComplete, onError } = {}) {
  if (!threadId) throw new Error('threadId required');

  const controller = new AbortController();
  let reader;
  let lastTextFragment = '';
  let currentEventType = null;

  const processText = (text) => {
    buffer += text;
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (line.startsWith('event:')) {
        currentEventType = line.substring(6).trim();
      } else if (line.startsWith('data:')) {
        const data = line.substring(5).trim();
        try {
          const parsed = safeParse(data);
          if (currentEventType === 'open') {
            try {
              onOpen && onOpen(parsed);
            } catch (_) {
              /* ignore */
            }
          } else if (currentEventType === 'tool_started') {
            try {
              onToken &&
                onToken({
                  type: 'tool_started',
                  tool_name: parsed?.tool_name || parsed?.metadata?.tool_name,
                  args: parsed?.args,
                  metadata: parsed?.metadata || {}
                });
            } catch (_) {
              /* ignore */
            }
          } else if (currentEventType === 'tool_output') {
            try {
              onToken &&
                onToken({
                  type: 'tool_output',
                  tool_name: parsed?.tool_name || parsed?.metadata?.tool_name,
                  result: parsed?.result,
                  metadata: parsed?.metadata || {}
                });
            } catch (_) {
              /* ignore */
            }
          } else if (currentEventType === 'tool_completed') {
            try {
              onToken &&
                onToken({
                  type: 'tool_completed',
                  tool_name: parsed?.tool_name || parsed?.metadata?.tool_name,
                  metadata: parsed?.metadata || {}
                });
            } catch (_) {
              /* ignore */
            }
          } else if (currentEventType === 'final') {
            const finalText = parsed?.message || parsed?.text || parsed?.content || '';
            if (finalText && finalText.length > lastTextFragment.length) lastTextFragment = finalText;
            try {
              onComplete && onComplete({ lastText: lastTextFragment, final: parsed });
            } catch (_) {
              /* ignore */
            }
          } else if (currentEventType === 'reasoning') {
            const textToken = parsed?.text || parsed?.delta || parsed?.content || '';
            if (textToken) onToken && onToken({ text: textToken, type: 'reasoning' });
          } else if (currentEventType === 'complete' || currentEventType === 'stopped') {
            try {
              onComplete && onComplete({ lastText: lastTextFragment });
            } catch (_) {
              /* ignore */
            }
          } else if (currentEventType === 'error') {
            // Emit structured error so UI can render a rich error message
            try {
              onToken && onToken({ type: 'error', ...parsed });
            } catch (_) {
              /* ignore */
            }
          } else {
            const norm = normalizeToken(parsed);
            if (norm?.text) {
              lastTextFragment += norm.text;
              onToken && onToken(norm);
            }
          }
        } catch (_) {
          // ignore parse errors
        }
        currentEventType = null;
      }
    }
  };

  let buffer = '';

  (async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const url = `${apiBase}/agentic/agent/orchestrate/subscribe/${encodeURIComponent(threadId)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        },
        credentials: 'include',
        signal: controller.signal
      });
      if (!res.ok || !res.body) {
        const err = new Error(`Subscribe failed (${res.status})`);
        try {
          onError && onError(err);
        } catch (_) {
          /* ignore */
        }
        return;
      }
      reader = res.body.getReader();
      const decoder = new TextDecoder();
      // Notify open when stream starts
      try {
        onOpen && onOpen();
      } catch (_) {
        /* ignore */
      }

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          try {
            onComplete && onComplete({ lastText: lastTextFragment });
          } catch (_) {
            /* ignore */
          }
          break;
        }
        const text = decoder.decode(value, { stream: true });
        processText(text);
      }
    } catch (err) {
      if (err?.name === 'AbortError') {
        // closed by caller; treat as silent complete
        try {
          onComplete && onComplete({ lastText: lastTextFragment });
        } catch (_) {
          /* ignore */
        }
      } else {
        try {
          onError && onError(err);
        } catch (_) {
          /* ignore */
        }
      }
    }
  })();

  return {
    close: () => {
      try {
        controller.abort();
      } catch (_) {
        /* ignore */
      }
      try {
        reader && reader.cancel && reader.cancel();
      } catch (_) {
        /* ignore */
      }
    }
  };
}

// Helpers
function safeParse(str) {
  if (!str) return {};
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
}

function normalizeToken(data) {
  if (data == null) return { text: '' };
  if (typeof data === 'string') return { text: data };
  const text = data.text || data.delta || data.content || data.message || '';
  const type = data.type || data.channel || data.kind;
  return { text, type };
}
